// The implementation runway: every engagement's milestone plan, and what is
// coming up next across all of them.
//
// A contract opens with milestones — on signing, at testing, at go-live — and
// settles into recurring revenue. The data for this has been complete since
// migration 0014: a position, a deliverable, a percentage of the fee, the SOW's
// month window, a baseline date and a signed slip. What there has never been is
// a screen showing more than one deal at a time, so "who is coming up on a
// billable milestone" was a question answered by opening five deal pages and
// remembering.
//
// TWO SHAPES, AND THE SECOND IS NOT A SORT OF THE FIRST. The band at the top is
// every milestone still ahead, across every engagement, soonest first — the
// question actually asked. The tracks below are each plan AS WRITTEN, in
// sequence order, which stops being date order the moment anything slips. A
// screen with only the tracks makes the reader interleave five plans by eye; a
// screen with only the band loses the shape of the engagement the milestone
// belongs to.
//
// FOUR STATES, DRAWN AS FOUR NODE SHAPES, and the distinction they carry is the
// one thing this screen adds to the data (0027):
//
//   billed     it went out. Filled, and the track behind it is solid.
//   reached    the deliverable landed, so nothing waits on delivery any more —
//              only on the invoice. Filled dark, and the loudest thing on the
//              page, because it is money sitting there.
//   confirmed  a date the customer agreed to. Outlined solid.
//   forecast   a date we picked so the money would stay visible. Outlined
//              DASHED, which is the same visual language the close screen uses
//              for a figure nobody knows — because it is the same kind of claim.
//
// COLOUR IS NEVER THE ONLY CUE: every node carries its state in words beneath
// it, and the two actions are buttons with verbs on them.
//
// A REACHED MILESTONE SAYS THE DAY IT WAS REACHED, never a date it bills on.
// When its fact names only the month, it says the month and claims no slip:
// dating it to the month's last day once made a SOW signed on its planned day
// read "two weeks late". A reached one also says what we bill for it — our
// share, the figure on the paper — and links to the close that bills it.

import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type {
  Currency,
  MilestoneState,
  RunwayEngagement,
  RunwayEvent,
  RunwayMilestone,
} from "@crm/shared";
import styles from "./RunwayPage.module.css";
import { FactEntryDialog, type FactPrefill } from "../facts/FactEntryDialog.js";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { Dialog } from "../../design/Dialog.js";
import { EmptyState, Notice, ProblemMessage, Spinner } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented } from "../../design/Segmented.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";
import { useDealFactMutations } from "../../api/mutations.js";
import { useMilestoneReachedFacts, useRunwayReport } from "../../api/queries.js";
import { describeSlip, formatCalendarDate, formatMonth } from "../../format/calendarDate.js";
import { formatCount, formatMoney, formatPercent } from "../../format/money.js";

const STATE: Record<MilestoneState, { label: string; tone: Tone; why: string }> = {
  billed: { label: "Billed", tone: "good", why: "It went out on a document." },
  reached: {
    label: "Reached",
    tone: "brand",
    why: "The deliverable landed. Nothing is waiting on delivery — only on the invoice.",
  },
  planned: {
    label: "Planned",
    tone: "neutral",
    why: "A date the plan carries. Nothing says the deliverable has landed, so it bills nothing yet.",
  },
};

const SCOPE_OPTIONS = [
  { value: "open" as const, label: "In flight", title: "Engagements with a milestone still ahead" },
  { value: "all" as const, label: "All", title: "Including plans that have finished billing" },
];

export function RunwayPage(): ReactNode {
  const [scope, setScope] = useState<"open" | "all">("open");
  /** The milestone somebody is recording as reached, or none. */
  const [reaching, setReaching] = useState<FactPrefill | null>(null);
  /** The milestone somebody is taking that claim back on, or none. */
  const [correcting, setCorrecting] = useState<{
    engagement: RunwayEngagement;
    milestone: RunwayMilestone;
  } | null>(null);

  const runway = useRunwayReport(null, scope === "all");

  usePageChrome({
    title: "Milestones",
  });

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Segmented
          label="Which engagements"
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={setScope}
        />
        <span className={styles.spacer} />
        <Link to="/billing/close" className={styles.toolbarLink}>
          To bill →
        </Link>
      </div>

      <QueryBoundary query={runway} label="the implementation runway">
        {(report) => (
          <>
            <StatTileRow>
              <StatTile
                label="Engagements"
                value={formatCount(report.counts.engagement_count)}
                hint={
                  report.counts.slipped_engagement_count === 0
                    ? "none has moved from its plan"
                    : `${formatCount(report.counts.slipped_engagement_count)} have slipped`
                }
              />
              <StatTile
                label="Billable now"
                value={formatCount(report.counts.reached_count)}
                hint={
                  report.counts.reached_count === 0
                    ? "nothing delivered and unbilled"
                    : "delivered, not yet invoiced"
                }
              />
              <StatTile
                label="On a date they agreed"
                value={formatCount(report.counts.confirmed_count)}
                hint="worth planning cash around"
              />
              <StatTile
                label="On a date we picked"
                value={formatCount(report.counts.forecast_count)}
                hint={
                  report.counts.forecast_count === 0
                    ? "every date is agreed"
                    : "nobody has committed to these"
                }
              />
            </StatTileRow>

            <NextEvents events={report.next_events} />

            {report.engagements.length === 0 ? (
              <Card>
                <EmptyState
                  title="No implementation plans"
                  description="A plan appears here once a term line bills against milestones. Give a line the milestone cadence on its deal page."
                />
              </Card>
            ) : (
              report.engagements.map((engagement) => (
                <EngagementCard
                  key={engagement.term_line_id}
                  engagement={engagement}
                  onReached={(milestone) => setReaching(reachedFact(engagement, milestone))}
                  onNotReached={(milestone) => setCorrecting({ engagement, milestone })}
                />
              ))
            )}
          </>
        )}
      </QueryBoundary>

      {reaching !== null && (
        <FactEntryDialog prefill={reaching} onClose={() => setReaching(null)} />
      )}

      {correcting !== null && (
        <NotReachedDialog
          engagement={correcting.engagement}
          milestone={correcting.milestone}
          onClose={() => setCorrecting(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Band 1 — what is next, across everything.
// ---------------------------------------------------------------------------

function NextEvents({ events }: { events: readonly RunwayEvent[] }): ReactNode {
  return (
    <Card>
      <CardHeader
        title="Next billable events"
        description="Every milestone still ahead, soonest first, across all engagements"
        actions={
          events.length === 0 ? undefined : (
            <Badge tone="neutral" shape="chip">
              {`${formatCount(events.length)} ahead`}
            </Badge>
          )
        }
      />
      <Table label="Next billable implementation events">
        <thead>
          <tr>
            <Th>Expected</Th>
            <Th>Customer</Th>
            <Th>Milestone</Th>
            <Th numeric>Bills</Th>
            <Th numeric>Our share</Th>
            <Th>Confidence</Th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 ? (
            <EmptyRow
              colSpan={6}
              title="Nothing ahead"
              description="Every milestone on every plan has been invoiced."
            />
          ) : (
            events.map((event) => (
              <tr key={event.id}>
                <Td mono nowrap>
                  {whenText(event)}
                  {slipText(event) !== null && (
                    <span className={styles.sub}>{slipText(event)}</span>
                  )}
                </Td>
                <Td strong>
                  <Link
                    to="/deals/$dealId"
                    params={{ dealId: event.deal_id }}
                    className={styles.dealLink}
                  >
                    {event.customer_name}
                  </Link>
                  <span className={styles.sub}>{`via ${event.partner_name}`}</span>
                </Td>
                <Td>
                  {event.label}
                  <span className={styles.sub}>
                    {`${event.seq} of ${event.milestone_count}`}
                    {event.plan_month_from !== null &&
                      event.plan_month_to !== null &&
                      ` · SOW ${planWindow(event.plan_month_from, event.plan_month_to)}`}
                  </span>
                </Td>
                <Td numeric>
                  {event.gross_minor === null ? (
                    <span className={styles.unknown}>no figure</span>
                  ) : (
                    formatMoney(event.gross_minor, event.currency)
                  )}
                </Td>
                <Td numeric muted>
                  {event.our_share_minor === null
                    ? "—"
                    : formatMoney(event.our_share_minor, event.currency, {
                        omitCurrencyCode: true,
                      })}
                </Td>
                <Td>
                  <Badge tone={STATE[event.state].tone} shape="chip">
                    {STATE[event.state].label}
                  </Badge>
                </Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Card>
  );
}

/** "Month 12" when the SOW's window spans one, "Months 6–7" when it spans more. */
function planWindow(from: number, to: number): string {
  return from === to ? `month ${from}` : `months ${from}–${to}`;
}

/** What a milestone's date line says about WHEN, the same on the band and on the plan. */
interface MilestoneWhen {
  state: RunwayMilestone["state"];
  due_date: string;
  reached_on: string | null;
  reached_period: string | null;
  days_shifted: number | null;
}

/**
 * The day it was reached, the month when that is all anybody recorded, or the
 * planned day while it has not been reached.
 */
function whenText(milestone: MilestoneWhen): string {
  if (milestone.reached_on !== null) return `Reached ${formatCalendarDate(milestone.reached_on)}`;
  if (milestone.reached_period !== null)
    return `Reached in ${formatMonth(milestone.reached_period)}`;
  return formatCalendarDate(milestone.due_date);
}

/**
 * How far it moved from the plan, in words, or nothing to say. A reached
 * milestone with a known day says "on plan" outright, because that is the
 * answer a reader looks for; a planned one says nothing until it has moved; and
 * one with no known day has no slip to state.
 */
function slipText(milestone: MilestoneWhen): string | null {
  if (milestone.days_shifted === null) return null;
  if (milestone.days_shifted !== 0) return describeSlip(milestone.days_shifted);
  return milestone.reached_on !== null ? describeSlip(0) : null;
}

// ---------------------------------------------------------------------------
// Band 2 — one engagement's plan, as written.
// ---------------------------------------------------------------------------

function EngagementCard({
  engagement,
  onReached,
  onNotReached,
}: {
  engagement: RunwayEngagement;
  onReached: (milestone: RunwayMilestone) => void;
  onNotReached: (milestone: RunwayMilestone) => void;
}): ReactNode {
  return (
    <Card>
      <CardHeader
        title={
          <Link
            to="/deals/$dealId"
            params={{ dealId: engagement.deal_id }}
            className={styles.dealLink}
          >
            {engagement.customer_name}
          </Link>
        }
        description={`via ${engagement.partner_name} · ${engagement.label}`}
        actions={
          <div className={styles.headerFigures}>
            <span className={styles.money}>
              {engagement.fee_minor === null
                ? "no fee stated"
                : `${formatMoney(engagement.fee_minor, engagement.currency)} fee`}
            </span>
            <span className={styles.moneyMuted}>
              {`${formatMoney(engagement.billed_minor, engagement.currency, { omitCurrencyCode: true })} billed · ${formatMoney(engagement.remaining_minor, engagement.currency, { omitCurrencyCode: true })} to go`}
            </span>
            {engagement.forecast_count > 0 && (
              <Badge tone="neutral" shape="chip">
                {`${formatCount(engagement.forecast_count)} forecast`}
              </Badge>
            )}
          </div>
        }
      />
      <div className={styles.trackScroll}>
        <ol className={styles.track}>
          {engagement.milestones.map((milestone) => (
            <Stop
              key={milestone.id}
              milestone={milestone}
              dealId={engagement.deal_id}
              partnerName={engagement.partner_name}
              currency={engagement.currency}
              onReached={onReached}
              onNotReached={onNotReached}
            />
          ))}
        </ol>
      </div>
      {engagement.pending_count > 0 && (
        <p className={styles.footnote}>
          {`${formatCount(engagement.pending_count)} milestone${engagement.pending_count === 1 ? " has" : "s have"} no figure, and ${engagement.pending_count === 1 ? "is" : "are"} in neither total above.`}
        </p>
      )}
    </Card>
  );
}

function Stop({
  milestone,
  dealId,
  partnerName,
  currency,
  onReached,
  onNotReached,
}: {
  milestone: RunwayMilestone;
  dealId: string;
  partnerName: string;
  currency: Currency;
  onReached: (milestone: RunwayMilestone) => void;
  onNotReached: (milestone: RunwayMilestone) => void;
}): ReactNode {
  const state = STATE[milestone.state];
  // A milestone bills in the period a `milestone_reached` fact names, and in no
  // other. So the one action here records that fact — there is no date to
  // confirm and no status to set. A billed milestone is history: its figure is
  // what an invoice says, and re-recording it would restate nothing.
  const canReach = milestone.state === "planned";
  // And the way back, while the claim is still only a claim: a fact recorded
  // against the wrong milestone drops it onto a month's close as billable, so
  // the screen that records it offers the correction too. A BILLED milestone
  // is past that — its figure went out on a document, and the way back from
  // one of those is a credit note, not this button.
  const canCorrect = milestone.state === "reached";

  return (
    <li className={`${styles.stop} ${styles[`stop--${milestone.state}`] ?? ""}`}>
      <span className={styles.node} aria-hidden="true" />
      <span className={styles.pct}>
        {milestone.pct === null ? `#${milestone.seq}` : formatPercent(milestone.pct)}
        {milestone.plan_month_from !== null &&
          milestone.plan_month_to !== null &&
          ` · ${planWindow(milestone.plan_month_from, milestone.plan_month_to)}`}
      </span>
      <span className={styles.label}>{milestone.label}</span>
      <span className={styles.date}>
        {whenText(milestone)}
        {slipText(milestone) !== null && (
          <span className={milestone.days_shifted === 0 ? styles.onPlan : styles.slip}>
            {slipText(milestone)}
          </span>
        )}
      </span>
      <span className={styles.amount}>
        {milestone.gross_minor === null
          ? "no figure"
          : formatMoney(milestone.gross_minor, currency, { omitCurrencyCode: true })}
        {milestone.invoice_number !== null && ` · ${milestone.invoice_number}`}
      </span>
      <span className={styles.stateWord}>
        <Badge tone={state.tone} shape="tag">
          {state.label}
        </Badge>
      </span>
      {/* Reached and not yet on a document: the money is sitting there, so
          the way to bill it is one press away, with the figure that goes on
          the paper — our share, not the customer's gross. */}
      {milestone.state === "reached" && milestone.reached_period !== null && (
        <span className={styles.billIt}>
          <Link
            to="/billing/close"
            search={{ period: milestone.reached_period, deal: dealId }}
            className={styles.billLink}
          >
            {milestone.our_share_minor === null
              ? `Bill ${partnerName} →`
              : `Bill ${partnerName} ${formatMoney(milestone.our_share_minor, currency, { omitCurrencyCode: true })} →`}
          </Link>
        </span>
      )}
      {canReach && (
        <span className={styles.actions}>
          <Button variant="secondary" onClick={() => onReached(milestone)}>
            Record it as reached
          </Button>
        </span>
      )}
      {canCorrect && (
        <span className={styles.actions}>
          {/* Named for the MILESTONE: a runway draws a dozen of these, and a
              dozen buttons all called "Not reached after all" tell a screen
              reader which engagement it is on and nothing else. */}
          <Button
            variant="subtle"
            onClick={() => onNotReached(milestone)}
            aria-label={`${milestone.label} has not been reached after all`}
          >
            Not reached after all
          </Button>
        </span>
      )}
    </li>
  );
}

/**
 * Taking back "this milestone landed".
 *
 * A fact has no PATCH: the row IS the claim, so the correction is to delete
 * it, and the row has to be found before it can be deleted. Nothing found is
 * an answer too — the milestone reads as reached and this screen cannot see
 * what made it so, which is worth saying rather than leaving a button that
 * does nothing.
 */
function NotReachedDialog({
  engagement,
  milestone,
  onClose,
}: {
  engagement: RunwayEngagement;
  milestone: RunwayMilestone;
  onClose: () => void;
}): ReactNode {
  const facts = useMilestoneReachedFacts(engagement.term_line_id, true);
  const mutations = useDealFactMutations();
  const fact = facts.data?.items.find((row) => row.milestone_seq === milestone.seq);

  if (fact !== undefined) {
    return (
      <DeleteDialog
        open
        onClose={onClose}
        title="Delete this fact"
        description={`The fact that says "${milestone.label}" landed in ${formatMonth(fact.period)} will be removed, and the milestone goes back to planned. It bills nothing until somebody records it again. A milestone already on a document is not corrected this way — that is a credit note.`}
        record={fact}
        remove={(id, version) => mutations.remove.mutateAsync({ id, version })}
        fetchCurrent={mutations.fetchCurrent}
        onDeleted={onClose}
      />
    );
  }

  return (
    <Dialog
      open
      narrow
      onClose={onClose}
      title="Not reached after all"
      footer={
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      }
    >
      {facts.isPending ? (
        <Spinner label="Finding the fact that says it landed…" inline />
      ) : facts.error !== null ? (
        <ProblemMessage error={facts.error} action="find the fact that says it landed" />
      ) : (
        <Notice tone="warn" title="Nothing here can take that back.">
          {`No recorded fact says "${milestone.label}" landed, so there is nothing to delete — and this screen changes a milestone no other way. Look at the deal's recorded facts, which is where every fact against this clause is listed.`}
        </Notice>
      )}
    </Dialog>
  );
}

/**
 * The fact that makes a milestone bill.
 *
 * The period is the milestone's own due month, because that is the month the
 * plan says it lands in — and the dialog lets a person move it when the
 * deliverable landed in a different one.
 */
function reachedFact(engagement: RunwayEngagement, milestone: RunwayMilestone): FactPrefill {
  return {
    dealId: engagement.deal_id,
    partnerId: engagement.partner_id,
    termLineId: engagement.term_line_id,
    period: milestone.due_date.slice(0, 7),
    kind: "milestone_reached",
    milestoneSeq: milestone.seq,
    unit: null,
    currency: engagement.currency,
    label: `${engagement.label} — ${milestone.label}`,
  };
}
